import anime from 'animejs'
import type { HTMLAttributes } from 'react'
import { useInViewEffect } from 'react-hook-inview'

import cn from '@/helper/cn'

import type { Stat } from '../constants/solutions'

type Props = HTMLAttributes<HTMLDivElement> & {
  content: string
  stats: Stat[]
}

const Stats = ({ content, stats, ...rest }: Props) => {
  const statsRef = useInViewEffect(
    ([entry], observer) => {
      if (entry.isIntersecting) {
        observer.unobserve(entry.target)

        anime
          .timeline({ loop: false })
          .add(
            {
              targets: 'h4',
              translateY: [100, 0],
              opacity: [0, 1],
              duration: 500,
              easing: 'easeOutQuad',
              delay: 250,
            },
            '-=250'
          )
          .add(
            {
              targets: '#stat-list .stat',
              translateX: [-100, 0],
              opacity: [0, 1],
              duration: 500,
              easing: 'easeOutQuad',
              delay: anime.stagger(100),
            },
            '-=250'
          )
          .add(
            {
              targets: '#stat-list .stat-grid-item',
              translateY: [100, 0],
              opacity: [0, 1],
              duration: 500,
              easing: 'easeOutQuad',
              delay: anime.stagger(100),
            },
            '-=250'
          )
      }
    },
    { threshold: 0.2 }
  )

  return stats.length >= 4 ? (
    <div
      ref={statsRef}
      {...rest}
      className="relative flex w-full flex-col rounded-xl "
    >
      <div className="rounded-t-xl bg-custom-blue px-8 py-3 lg:px-24 lg:py-12">
        <h4 className="text-center text-xs font-medium leading-normal text-brand-50 opacity-0 [text-wrap:balance] lg:text-xl">
          {content}
        </h4>
      </div>
      <div
        id="stat-list"
        className="grid grid-cols-2 rounded-b-xl bg-brand-50 px-3 md:flex md:px-4"
      >
        {stats.map(stat => (
          <div
            key={stat.value}
            className="stat-grid-item flex flex-1 flex-col items-center justify-start rounded-xl py-4 lg:py-8"
          >
            <span className="text-[2.5rem] font-semibold text-custom-blue lg:text-[5rem]">
              {stat.value}
            </span>
            <p className="max-w-[66%] text-center text-[.5rem] text-zinc-900 [text-wrap:balance] md:max-w-[75%] lg:text-lg">
              {stat.info}
            </p>
          </div>
        ))}
      </div>
    </div>
  ) : (
    <div
      ref={statsRef}
      {...rest}
      className="relative flex w-full flex-col justify-between md:flex-row"
    >
      <div className="flex items-center rounded-t-xl bg-custom-blue px-10 py-8 md:rounded-l-xl md:rounded-tr-none">
        <h4 className="text-xs font-medium leading-normal text-brand-50 [text-wrap:balance] max-md:text-center lg:text-xl">
          {content}
        </h4>
      </div>
      <div
        id="stat-list"
        className="max-md:grid max-md:[grid-template-columns:repeat(auto-fit,minmax(0,1fr))] md:flex"
      >
        {stats.map((stat, idx) => (
          <div
            key={`${stat.value}-${idx}`}
            className={cn(
              'py-8 md:min-w-[14rem] lg:min-w-[18rem]',
              idx % 2 === 0 ? 'bg-brand-50' : 'bg-brand-75',
              idx === 0 && 'max-md:rounded-l-xl',
              idx === stats.length - 1 && 'rounded-r-xl'
            )}
          >
            <div className="stat flex flex-1 flex-col items-center justify-start">
              <span className="text-[2.5rem] font-semibold text-custom-blue lg:text-[5rem]">
                {stat.value}
              </span>
              <p className="max-w-[66%] text-center text-[.5rem] text-zinc-900 [text-wrap:balance] md:max-w-[75%] lg:text-lg">
                {stat.info}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// const StatsOldVersion = ({ content, stats, ...rest }: Props) => {
//   return (
// <div
//   {...rest}
//   className="relative w-full rounded-xl bg-custom-blue px-10 py-8"
// >
//   <h4 className="max-w-[33%] text-xl font-semibold leading-normal text-white [text-wrap:pretty]">
//     {content}
//   </h4>
//   <div
//     className={cn(
//       'absolute -top-8 right-8 grid grid-flow-col-dense grid-rows-2 rounded-xl bg-brand-50'
//     )}
//   >
//     {stats.map((stat, idx) => (
//       <div
//         key={`${stat.value}-${idx}`}
//         className={cn(
//           'flex min-w-[16rem] flex-col items-center justify-center py-8',
//           idx % 4 === 0 || idx % 4 === 3 ? 'bg-brand-50' : 'bg-brand-75',
//           idx === 0 && 'rounded-tl-xl',
//           idx === 1 && 'rounded-bl-xl',
//           idx === stats.length - 1 && 'rounded-br-xl',
//           idx === stats.length - 2 && 'rounded-tr-xl'
//         )}
//       >
//         <span className="text-[1.75rem] font-semibold text-custom-blue">
//           {stat.value}
//         </span>
//         <p className="text-lg text-zinc-900">{stat.info}</p>
//       </div>
//     ))}
//   </div>
// </div>
//   )
// }

export default Stats
